'use client';

import { useMemo, useState } from 'react';
import { Play } from 'lucide-react';

import type { PostMedia } from '@kit/community';
import { cn } from '@kit/ui/cn';
import { ImageLightbox, type ViewerImage } from '@kit/ui/image-viewer';

// ---------------------------------------------------------------------------
// Media grid
// ---------------------------------------------------------------------------

function isVideo(item: PostMedia): boolean {
  return item.mediaType === 'video';
}

/** Display source for a grid tile: videos show their poster. */
function tileSrc(item: PostMedia): string | null {
  return isVideo(item) ? (item.thumbnailUrl ?? item.url) : item.url;
}

function MediaTile({
  item,
  className,
  overlayCount,
  onOpen,
}: {
  item: PostMedia;
  className?: string;
  overlayCount?: number;
  onOpen: () => void;
}) {
  const src = tileSrc(item);
  if (!src) return null;

  return (
    // eslint-disable-next-line kit-components/use-kit-primitives -- media tile: structural wrapper around a full-bleed image sized by the parent's aspect-ratio; @kit/ui Button's fixed h-9 + padding + inline-flex chrome would break the media-grid tile
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'group relative block w-full overflow-hidden focus-visible:outline-2',
        className,
      )}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt=""
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
      />
      {isVideo(item) && (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-md bg-black/60 backdrop-blur-sm">
            <Play
              className="ml-0.5 h-5 w-5 fill-white text-white"
              aria-hidden="true"
            />
          </span>
        </span>
      )}
      {overlayCount !== undefined && overlayCount > 0 && (
        <span className="absolute inset-0 flex items-center justify-center bg-black/50 text-xl font-semibold text-white">
          +{overlayCount}
        </span>
      )}
    </button>
  );
}

/**
 * Rounded media grid (Threads/X-style): 1 = natural up to 4:5; 2 = two
 * columns; 3 = one tall + two stacked; 4+ = 2×2 with a "+N" overlay.
 * Images open in the shared lightbox; video tiles show a poster + play
 * glyph and open in the lightbox as well (poster; playback happens on the
 * media's own page — no inline autoplay by design).
 */
export function PostMediaGrid({
  media,
  identity,
}: {
  media: PostMedia[];
  // Floating top-left identity chip (post author) for full-bleed usage.
  identity?: React.ReactNode;
}) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const resolved = useMemo(
    () => media.filter((item) => Boolean(tileSrc(item))),
    [media],
  );

  const viewerImages = useMemo<ViewerImage[]>(
    () =>
      resolved.map((item) => ({
        id: item.id,
        src: tileSrc(item)!,
        thumbnailSrc: item.thumbnailUrl ?? undefined,
      })),
    [resolved],
  );

  if (resolved.length === 0) return null;

  const openAt = (index: number) => () => setLightboxIndex(index);
  const count = resolved.length;

  let grid: React.ReactNode;
  if (count === 1) {
    const item = resolved[0]!;
    // Reserve space from known dimensions; cap tall portraits at 4:5.
    const ratio =
      item.width && item.height
        ? Math.max(item.width / item.height, 4 / 5)
        : 16 / 9;
    grid = (
      <div style={{ aspectRatio: `${ratio}` }}>
        <MediaTile item={item} className="h-full" onOpen={openAt(0)} />
      </div>
    );
  } else if (count === 2) {
    grid = (
      <div className="grid aspect-[16/9] grid-cols-2 gap-0.5">
        {resolved.map((item, i) => (
          <MediaTile key={item.id} item={item} onOpen={openAt(i)} />
        ))}
      </div>
    );
  } else if (count === 3) {
    grid = (
      <div className="grid aspect-[16/9] grid-cols-2 gap-0.5">
        <MediaTile item={resolved[0]!} onOpen={openAt(0)} />
        <div className="grid grid-rows-2 gap-0.5">
          <MediaTile item={resolved[1]!} onOpen={openAt(1)} />
          <MediaTile item={resolved[2]!} onOpen={openAt(2)} />
        </div>
      </div>
    );
  } else {
    grid = (
      <div className="grid aspect-square grid-cols-2 grid-rows-2 gap-0.5">
        {resolved.slice(0, 4).map((item, i) => (
          <MediaTile
            key={item.id}
            item={item}
            onOpen={openAt(i)}
            overlayCount={i === 3 ? count - 4 : undefined}
          />
        ))}
      </div>
    );
  }

  return (
    <>
      <div
        className={
          identity
            ? 'relative overflow-hidden'
            : 'relative overflow-hidden rounded-lg'
        }
      >
        {identity}
        {grid}
      </div>
      <ImageLightbox
        images={viewerImages}
        index={lightboxIndex ?? 0}
        onIndexChange={setLightboxIndex}
        open={lightboxIndex !== null}
        onOpenChange={(open) => {
          if (!open) setLightboxIndex(null);
        }}
      />
    </>
  );
}
